import { z } from "zod";

import { FilledDynamicValueTableColumnTypeLiteral } from "../DynamicValue.js";
import { DisplayTableColumnId } from "../idTypeBrands.js";
import { ScopeItemType } from "../scopeTypes.js";
import { DataType } from "../semantic-layer/generated/hex_sl_schema.js";
import {
  typedObjectEntries,
  typedObjectFromEntries,
} from "../utils/typedObjects.js";

import { zBrandedRecord } from "../zod/utils.js";
export const CalcColumnDefinition = z.object({
  name: DisplayTableColumnId,
  expression: z.string(),
  // `muted` is no longer supported, it's just around for backwards compatibility

  muted: z.boolean().nullable().optional(),
});
export type CalcColumnDefinition = z.infer<typeof CalcColumnDefinition>;

export const CalcReferencedColumnTypes = zBrandedRecord<
  DisplayTableColumnId,
  typeof FilledDynamicValueTableColumnTypeLiteral
>(FilledDynamicValueTableColumnTypeLiteral);
export type CalcReferencedColumnTypes = z.infer<
  typeof CalcReferencedColumnTypes
>;

export const CalcReferencedColumn = z.object({
  columnId: DisplayTableColumnId,
  columnType: FilledDynamicValueTableColumnTypeLiteral,
  containsAggregate: z.boolean(),
});
export type CalcReferencedColumn = z.infer<typeof CalcReferencedColumn>;

/** The set of ScopeItemTypes that calcs expressions can handle */
export const CalcScopeItemTypeSubset = z.enum([
  "STRING",
  "NUMBER",
  "BOOLEAN",
  "DATE",
  "DATETIME",
]);
export type CalcScopeItemTypeSubset = z.infer<typeof CalcScopeItemTypeSubset>;
// make sure `CalcScopeItemTypeSubset` is actually a subset of `ScopeItemType`
null! as CalcScopeItemTypeSubset satisfies ScopeItemType;

export const CalcReferencedParamTypes = z.record(
  z.string(),
  CalcScopeItemTypeSubset,
);
export type CalcReferencedParamTypes = z.infer<typeof CalcReferencedParamTypes>;

export const CalcsDefinition = z.object({
  calcColumns: z.array(CalcColumnDefinition),
  referencedColumns: z.array(CalcReferencedColumn).optional(),
  referencedParamTypes: CalcReferencedParamTypes.nullable().optional(),
  // @deprecated - use `referencedColumns` after applying `normalizeCalcsDefinition`
  referencedColumnTypes: CalcReferencedColumnTypes,
});
export type CalcsDefinition = z.infer<typeof CalcsDefinition>;

export const EMPTY_CALCS: CalcsDefinition = {
  calcColumns: [],
  referencedColumns: [],
  referencedColumnTypes: {},
};

/**
 * Normalize the provided calcs definition in place, ensuring all fields
 * are present and up to date.
 */
export function normalizeCalcsDefinition(
  source: CalcsDefinition,
  inPlace = false,
): Required<CalcsDefinition> {
  const result: Required<CalcsDefinition> = inPlace
    ? (source as Required<CalcsDefinition>)
    : {
        referencedParamTypes: source.referencedParamTypes ?? {},
        referencedColumns: [],
        ...source,
      };

  // ensure `referencedColumnTypes` is up to date with the source of truth
  // `referencedColumns` field.
  if (source.referencedColumns) {
    result.referencedColumnTypes = typedObjectFromEntries(
      source.referencedColumns.map((column) => [
        column.columnId,
        column.columnType,
      ]),
    );
  } else {
    // in this case, the calc definition was created before `.referencedColumns`
    // was introduced, so we need to migrate it in.
    result.referencedColumns = typedObjectEntries(
      source.referencedColumnTypes,
    ).map(([columnId, columnType]) => ({
      columnId,
      columnType,
      // before `.referencedColumns` was introduced, all column references
      // were non-aggregates
      containsAggregate: false,
    }));
  }
  return result;
}

export const CALC_TYPE_TO_HQL_TYPE: {
  [K in CalcScopeItemTypeSubset]: DataType;
} = {
  STRING: "string",
  NUMBER: "number",
  BOOLEAN: "boolean",
  DATE: "date",
  DATETIME: "timestamptz",
} as const;
